import { isEmpty, mapValues, omit } from 'mojito/utils';
import { combineReducers } from 'redux';
import { initState, resetState } from './actions';

/**
 * Class manages redux reducers. Allows to add and remove reducers dynamically. Expose {@link Mojito.Core.Services.ReducerManager#reduce|reduce} function
 * to be used on store creation.
 *
 * @class ReducerManager
 * @memberof Mojito.Core.Services
 */
export default class ReducerManager {
    constructor(reducers, state) {
        this.reducers = reducers || {};
        this.initialState = state || {};
        this.recentlyAddedReducers = {};
        this.recentlyRemovedReducers = [];
        const appReducer = !isEmpty(reducers) ? combineReducers(reducers) : reducer => reducer;
        this.reducerFunc = this.createRootReducer(appReducer);
    }

    /**
     * Adds reducer.
     *
     * @param {string} key - The path to the state property, see {@link https://www.npmjs.com/package/object-path|object-path}.
     * @param {Function} reducer - Reducer function.
     *
     * @function Mojito.Core.Services.ReducerManager#add
     */
    add(key, reducer) {
        this.reducers[key] = reducer;
        this.recentlyAddedReducers[key] = reducer;
        const newReducer = combineReducers(this.reducers);
        this.reducerFunc = this.createRootReducer(newReducer);
    }

    /**
     * Removes reducer.
     *
     * @param {string} key - The key used during reducer {@link Mojito.Core.Services.ReducerManager#add|add}.
     *
     * @function Mojito.Core.Services.ReducerManager#remove
     */
    remove(key) {
        delete this.reducers[key];
        this.recentlyRemovedReducers.push(key);
        const newReducer = combineReducers(this.reducers);
        this.reducerFunc = this.createRootReducer(newReducer);
    }

    /**
     * Reduce function. Can be used during store creation.
     *
     * @param {object} state - Redux state.
     * @param {Mojito.Core.Services.redux.Action} action - Redux action.
     *
     * @returns {object} Returns resulting state after being reduced.
     *
     * @function Mojito.Core.Services.ReducerManager#reduce
     */
    reduce(state, action) {
        // If any reducers have been removed, clean up their state
        if (this.recentlyRemovedReducers.length > 0) {
            state = omit(state, this.recentlyRemovedReducers);
            this.recentlyRemovedReducers = [];
        }
        if (!isEmpty(this.recentlyAddedReducers)) {
            const initialState = mapValues(this.recentlyAddedReducers, reducer =>
                reducer(undefined, initState)
            );
            this.initialState = { ...this.initialState, ...initialState };
            this.recentlyAddedReducers = {};
        }
        return this.reducerFunc(state, action);
    }

    createRootReducer(appReducer) {
        return (state, action) => {
            switch (action.type) {
                case `${initState}`:
                    return action.payload;
                case `${resetState}`:
                    return this.initialState;
                default:
                    return appReducer(state, action);
            }
        };
    }
}
